import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Image as ImageIcon, FileText, Upload, Link as LinkIcon, List, Monitor, Loader2, Download, Sparkles, CheckSquare, Package, CheckCircle, ListChecks, ChevronUp, ChevronDown, Smartphone } from 'lucide-react';
import { Product, FormFieldDefinition, ProductDocument, PresentationSlide } from '../types';
import { Button } from './ui/Button';
import { supabase } from '../supabaseClient';
import { ProductWriter } from './ProductWriter';
import {RewriteImprove} from './RewriteImprove';
import {productRewriteContext} from '../rewriteContext';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onSaveSuccess: () => void;
}

export const ProductModal: React.FC<ProductModalProps> = ({ isOpen, onClose, product, onSaveSuccess }) => {
  const [activeTab, setActiveTab] = useState<'DETAILS' | 'PRODUCTION'>('DETAILS');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [writerOpen, setWriterOpen] = useState(false);
  
  const docInputRef = useRef<HTMLInputElement>(null);
  const [activeDocUploadId, setActiveDocUploadId] = useState<string | null>(null);

  const slideInputRef = useRef<HTMLInputElement>(null);
  const [activeSlideUploadId, setActiveSlideUploadId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Product & { equipmentList?: string[], auto_tasks?: string, requiresSharingConfig?: boolean }>({
    id: '',
    name: '',
    description: '',
    price: 0,
    image: '',
    tags: [],
    longDescription: '',
    features: [],
    specs: {},
    gallery: [],
    productionGuide: '',
    uploadFolderUrl: '',
    documents: [],
    onboardingFields: [],
    presentationSlides: [],
    equipmentList: [],
    auto_tasks: '', 
    requiresSharingConfig: false, // ⚡ ADDED: Initial state
    slideTitle: '',
    slideDescription: ''
  });

  useEffect(() => {
    if (isOpen) {
        setWriterOpen(false);
        if (product) {
            setFormData({ 
               ...product,
               price: product.price || (product as any).base_price || 0,
               equipmentList: (product as any).equipment_list || (product as any).equipmentList || [],
               auto_tasks: (product as any).auto_tasks || '', 
               requiresSharingConfig: (product as any).requires_sharing_config || false, // ⚡ ADD THIS LINE HERE!
               longDescription: (product as any).long_description || product.longDescription || '',
               productionGuide: (product as any).production_guide || product.productionGuide || '',
               uploadFolderUrl: (product as any).upload_folder_url || product.uploadFolderUrl || '',
               onboardingFields: (product as any).onboarding_fields || product.onboardingFields || [],
               presentationSlides: ((product as any).presentation_slides || product.presentationSlides || []).map((s: any) => ({
                   id: s.id,
                   type: s.type,
                   url: s.url,
                   title: s.title || '',
                   caption: s.description || s.caption || ''
               })),
               slideTitle: (product as any).slide_title || product.slideTitle || '',
               slideDescription: (product as any).slide_description || product.slideDescription || ''
            });
          }
      setActiveTab('DETAILS');
    }
  }, [isOpen, product]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
        const payload = {
            name: formData.name,
            base_price: formData.price,
            description: formData.description,
            image: formData.image,
            tags: formData.tags,
            long_description: formData.longDescription,
            features: formData.features,
            specs: formData.specs,
            gallery: formData.gallery,
            production_guide: formData.productionGuide,
            upload_folder_url: formData.uploadFolderUrl,
            documents: formData.documents,
            onboarding_fields: formData.onboardingFields,
            equipment_list: (formData as any).equipmentList || [],
            auto_tasks: formData.auto_tasks || '', // ⚡ ADDED: Save to DB
            requires_sharing_config: (formData as any).requiresSharingConfig || false,
            slide_title: formData.slideTitle,
            slide_description: formData.slideDescription,
            presentation_slides: formData.presentationSlides?.map(s => ({
                id: s.id,
                type: s.type,
                url: s.url,
                title: s.title || '',
                description: s.caption || ''
            })) || [],
        };

        if (formData.id && !formData.id.startsWith('prod-')) {
            const { error } = await supabase.from('products').update(payload).eq('id', formData.id);
            if (error) throw error;
        } else {
            const { error } = await supabase.from('products').insert([payload]);
            if (error) throw error;
        }
        
        onSaveSuccess();
        onClose();
    } catch (error) {
        console.error("Error saving product:", error);
        alert("Failed to save product. Check console for details.");
    } finally {
        setIsSaving(false);
    }
  };

  // --- Upload Helper ---
  const uploadFile = async (file: File, folder: string): Promise<string | null> => {
      setIsUploading(true);
      try {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
          const filePath = `${folder}/${fileName}`;

          const { error: uploadError } = await supabase.storage
              .from('product-media')
              .upload(filePath, file);

          if (uploadError) throw uploadError;

          const { data } = supabase.storage
              .from('product-media')
              .getPublicUrl(filePath);

          return data.publicUrl;
      } catch (error) {
          console.error("Upload error:", error);
          alert("Failed to upload file. Please check your connection.");
          return null;
      } finally {
          setIsUploading(false);
      }
  };
  const addEquipment = (item: string) => {
    if (item && !(formData as any).equipmentList?.includes(item)) {
        setFormData(prev => ({ ...prev, equipmentList: [...((prev as any).equipmentList || []), item] } as any));
    }
 };

 const removeEquipment = (item: string) => {
    setFormData(prev => ({ ...prev, equipmentList: ((prev as any).equipmentList || []).filter((i: string) => i !== item) } as any));
 };

  // --- Array Helpers ---

  const addTag = (tag: string) => {
     if (tag && !formData.tags?.includes(tag)) {
         setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), tag] }));
     }
  };

  const removeTag = (tag: string) => {
     setFormData(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }));
  };

  const addFeature = () => {
      setFormData(prev => ({ ...prev, features: [...(prev.features || []), 'New Feature'] }));
  };

  const updateFeature = (index: number, value: string) => {
      const newFeatures = [...(formData.features || [])];
      newFeatures[index] = value;
      setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const removeFeature = (index: number) => {
      setFormData(prev => ({ ...prev, features: (formData.features || []).filter((_, i) => i !== index) }));
  };

  // Slides
  const addSlide = () => {
    const newSlide: PresentationSlide = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'image',
      url: '',
      title: '',
      caption: ''
    };
    setFormData(prev => ({
      ...prev,
      presentationSlides: [...(prev.presentationSlides || []), newSlide]
    }));
  };

  const removeSlide = (id: string) => {
    setFormData(prev => ({
      ...prev,
      presentationSlides: prev.presentationSlides?.filter(s => s.id !== id)
    }));
  };

  const updateSlide = (id: string, updates: Partial<PresentationSlide>) => {
    setFormData(prev => ({
      ...prev,
      presentationSlides: prev.presentationSlides?.map(s => s.id === id ? { ...s, ...updates } : s)
    }));
  };

  const handleSlideUploadClick = (slideId: string) => {
    setActiveSlideUploadId(slideId);
    slideInputRef.current?.click();
  };

  const handleSlideFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && activeSlideUploadId) {
      const file = e.target.files[0];
      const publicUrl = await uploadFile(file, 'slides');
      if (publicUrl) {
        updateSlide(activeSlideUploadId, { url: publicUrl });
      }
      setActiveSlideUploadId(null);
      if (slideInputRef.current) slideInputRef.current.value = '';
    }
  };

  // Docs
  const addDocument = () => {
    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), { id: Math.random().toString(36).substr(2, 9), name: 'New Document', type: 'PDF', url: '' }]
    }));
  };

  const removeDocument = (id: string) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents?.filter(d => d.id !== id)
    }));
  };

  const updateDocument = (id: string, updates: Partial<ProductDocument>) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents?.map(d => d.id === id ? { ...d, ...updates } : d)
    }));
  };

  const handleDocUploadClick = (docId: string) => {
    setActiveDocUploadId(docId);
    docInputRef.current?.click();
  };

  const handleDocFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && activeDocUploadId) {
      const file = e.target.files[0];
      const publicUrl = await uploadFile(file, 'documents');
      if (publicUrl) {
        updateDocument(activeDocUploadId, { name: file.name, url: publicUrl });
      }
      setActiveDocUploadId(null);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  // Fields
  const addField = () => {
      const newField: FormFieldDefinition = {
          id: Math.random().toString(36).substr(2, 9),
          label: 'New Field',
          type: 'text',
          required: false
      };
      setFormData(prev => ({ ...prev, onboardingFields: [...(prev.onboardingFields || []), newField] }));
  };
  
  const updateField = (index: number, updates: Partial<FormFieldDefinition>) => {
    const newFields = [...(formData.onboardingFields || [])];
    newFields[index] = { ...newFields[index], ...updates };
    setFormData(prev => ({ ...prev, onboardingFields: newFields }));
};

const removeField = (index: number) => {
    setFormData(prev => ({ ...prev, onboardingFields: (formData.onboardingFields || []).filter((_, i) => i !== index) }));
};

// ⚡ NEW: FUNCTION TO SHUFFLE QUESTIONS UP AND DOWN
const moveField = (index: number, direction: 'up' | 'down') => {
    if (!formData.onboardingFields) return;
    const newFields = [...formData.onboardingFields];
    
    if (direction === 'up' && index > 0) {
        [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    } else if (direction === 'down' && index < newFields.length - 1) {
        [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    }
    
    setFormData(prev => ({ ...prev, onboardingFields: newFields }));
};

if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
      <div className="bg-slate-900 w-full max-w-4xl rounded-2xl border border-white/10 flex flex-col shadow-2xl relative max-h-[90vh] animate-fade-in-up">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10 bg-slate-950/50 shrink-0 rounded-t-2xl">
          <h3 className="text-xl font-display font-bold text-white">
            {product ? 'Edit Product' : 'Add New Product'}
          </h3>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex px-6 border-b border-white/10 bg-slate-900 shrink-0">
          <button 
            onClick={() => setActiveTab('DETAILS')} 
            className={`py-4 mr-6 text-sm font-bold border-b-2 transition-colors ${activeTab === 'DETAILS' ? 'text-brand-neonBlue border-brand-neonBlue' : 'text-slate-500 border-transparent hover:text-white'}`}
          >
            Details & Media
          </button>
          <button 
            onClick={() => setActiveTab('PRODUCTION')} 
            className={`py-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'PRODUCTION' ? 'text-brand-neonPink border-brand-neonPink' : 'text-slate-500 border-transparent hover:text-white'}`}
          >
            Production & Onboarding
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {writerOpen ? <ProductWriter product={formData} onClose={()=>setWriterOpen(false)} onApply={patch=>{setFormData(prev=>({...prev,...patch}));setWriterOpen(false);}}/> : <>
          
          {/* TAB 1: DETAILS */}
          {activeTab === 'DETAILS' && (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <div className="flex justify-between items-end mb-1">
                           <label className="block text-xs font-bold text-slate-400 uppercase">Product Name</label>
                           <button 
                              onClick={()=>setWriterOpen(true)}
                              disabled={!formData.name}
                              className="text-[10px] font-bold bg-brand-neonBlue/10 text-brand-neonBlue border border-brand-neonBlue/30 px-2 py-1 rounded-md flex items-center gap-1 hover:bg-brand-neonBlue hover:text-black transition-all disabled:opacity-50"
                           >
                              <Sparkles size={12}/> Product Writer
                           </button>
                        </div>
                        <input className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-white focus:border-brand-neonBlue outline-none" placeholder="e.g. 360 Video Booth" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Base Price ($)</label>
                        <input type="number" className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-white focus:border-brand-neonBlue outline-none" placeholder="1500" value={formData.price} onChange={e => setFormData({...formData, price: parseFloat(e.target.value) || 0})} />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Main Image URL</label>
                    <div className="flex gap-2">
                        <input className="flex-1 bg-slate-950 border border-slate-700 rounded p-2 text-white" value={formData.image} onChange={e => setFormData({...formData, image: e.target.value})} />
                        <div className="w-10 h-10 bg-slate-800 rounded border border-white/10 overflow-hidden shrink-0">
                        {formData.image && <img src={formData.image} alt="Product" className="w-full h-full object-contain" />}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Tags (Comma separated)</label>
                    <input 
                        className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-white" 
                        placeholder="Type and press enter"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addTag(e.currentTarget.value);
                                e.currentTarget.value = '';
                            }
                        }}
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                        {formData.tags?.map(tag => (
                            <span key={tag} className="text-xs bg-slate-800 text-white px-2 py-1 rounded flex items-center gap-1">
                                {tag} <button onClick={() => removeTag(tag)}><X size={10}/></button>
                            </span>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Short Description</label>
                    <textarea aria-label="Product short description" className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-white" rows={3} value={formData.description} onChange={e=>setFormData({...formData,description:e.target.value})}/>
                    <RewriteImprove label="Product short description" text={formData.description||''} context={productRewriteContext(formData)} names={[formData.name]} onApply={value=>setFormData(p=>({...p,description:value}))}/>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Long Description</label>
                    <textarea className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-white" rows={4} value={formData.longDescription} onChange={e => setFormData({...formData, longDescription: e.target.value})} />
                    <RewriteImprove label="Product proposal description" text={formData.longDescription||''} context={productRewriteContext(formData)} names={[formData.name]} onApply={value=>setFormData(p=>({...p,longDescription:value}))}/>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-xs font-bold text-slate-400 uppercase">Key Features</label>
                        <Button size="sm" variant="outline" onClick={addFeature}><Plus size={12} className="mr-1"/> Add</Button>
                    </div>
                    <div className="space-y-2">
                        {formData.features?.map((feat, idx) => (
                            <div key={idx} className="flex gap-2">
                                <input className="flex-1 bg-slate-950 border border-slate-700 rounded p-2 text-white text-sm" value={feat} onChange={e => updateFeature(idx, e.target.value)} />
                                <button onClick={() => removeFeature(idx)} className="text-slate-500 hover:text-red-400"><Trash2 size={16}/></button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Presentation Slides Section */}
                <div className="pt-6 border-t border-white/5">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase flex items-center gap-2">
                            <Monitor size={14} className="text-brand-neonBlue"/> Presentation Slides
                        </h4>
                        <Button size="sm" variant="outline" onClick={addSlide}>
                            <Plus size={12} className="mr-1"/> Add Slide
                        </Button>
                    </div>
                    <div className="space-y-4">
                        {formData.presentationSlides?.map((slide) => (
                            <div key={slide.id} className="bg-slate-950 border border-white/10 rounded-lg p-4 relative group">
                                <button 
                                    onClick={() => removeSlide(slide.id)} 
                                    className="absolute top-2 right-2 text-slate-500 hover:text-red-500 p-1 z-10"
                                >
                                    <X size={14}/>
                                </button>
                                
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                    <div className="md:col-span-3 space-y-2">
                                        <select 
                                            className="w-full bg-slate-900 border border-slate-700 rounded p-1 text-white text-xs"
                                            value={slide.type}
                                            onChange={(e) => updateSlide(slide.id, { type: e.target.value as 'image' | 'video' })}
                                        >
                                            <option value="image">Image</option>
                                            <option value="video">Video</option>
                                        </select>
                                        <div className="aspect-video bg-slate-900 rounded border border-white/5 overflow-hidden flex items-center justify-center">
                                            {slide.url ? (
                                                slide.type === 'video' ? (
                                                    <video 
                                                        className="w-full h-full object-cover" 
                                                        controls 
                                                        playsInline 
                                                        muted
                                                        preload="metadata"
                                                        onError={(e) => console.error("Preview video error:", e)}
                                                    >
                                                        <source src={slide.url} type="video/mp4" />
                                                        Video not supported
                                                    </video>
                                                ) : (
                                                    <img src={slide.url} alt="Slide" className="w-full h-full object-cover" />
                                                )
                                            ) : (
                                                <ImageIcon size={20} className="text-slate-600"/>
                                            )}
                                        </div>
                                    </div>
                                    <div className="md:col-span-9 space-y-2">
                                        <div className="flex gap-2">
                                            <input 
                                                className="flex-1 w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-xs"
                                                value={slide.url}
                                                onChange={(e) => updateSlide(slide.id, { url: e.target.value })}
                                                placeholder="Media URL or Upload"
                                            />
                                            <button 
                                                onClick={() => handleSlideUploadClick(slide.id)}
                                                className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-white text-xs rounded border border-white/10 flex items-center gap-1"
                                                disabled={isUploading}
                                            >
                                                <Upload size={12}/> {isUploading && activeSlideUploadId === slide.id ? '...' : 'Upload'}
                                            </button>
                                        </div>
                                        <input 
                                            className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-xs"
                                            value={slide.title || ''}
                                            onChange={(e) => updateSlide(slide.id, { title: e.target.value })}
                                            placeholder="Slide Title"
                                        />
                                        <textarea 
                                            className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-xs"
                                            rows={2}
                                            value={slide.caption || ''}
                                            onChange={(e) => updateSlide(slide.id, { caption: e.target.value })}
                                            placeholder="Caption / Description"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <input type="file" ref={slideInputRef} className="hidden" onChange={handleSlideFileChange} accept="image/*,video/*" />
                </div>
            </div>
          )}

{/* TAB 2: PRODUCTION */}
{activeTab === 'PRODUCTION' && (
             <div className="space-y-6">

                {/* ⚡ THE EQUIPMENT LIST */}
                <div className="bg-slate-900/50 p-5 rounded-xl border border-white/5 shadow-inner">
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-2">
                        <Package size={16} className="text-brand-neonPink"/> Standard Equipment & Packing List
                    </label>
                    <p className="text-[10px] text-slate-500 mb-3">These are the standard defaults used by Refresh from Current Product Inventory. Review and apply changes to the Draft Equipment List, then use Confirm Equipment.</p>
                    <input 
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-white focus:border-brand-neonPink outline-none" 
                        placeholder="Type gear name and press Enter (e.g. 10m Extension Cable)"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addEquipment(e.currentTarget.value.trim());
                                e.currentTarget.value = '';
                            }
                        }}
                    />
                    <div className="flex flex-wrap gap-2 mt-4">
                        {((formData as any).equipmentList || []).map((item: string) => (
                            <span key={item} className="text-[11px] font-bold tracking-wide bg-slate-800 border border-slate-600 text-white px-3 py-1.5 rounded-full flex items-center gap-2 shadow-md transition-all hover:border-brand-neonPink">
                                {item} 
                                <button onClick={() => removeEquipment(item)} className="text-slate-500 hover:text-brand-neonPink transition-colors">
                                    <X size={14}/>
                                </button>
                            </span>
                        ))}
                    </div>
                </div>

                {/* ⚡ AUTO-TASKS INPUT ⚡ */}
                <div className="space-y-2 mt-4 bg-brand-neonBlue/5 border border-brand-neonBlue/20 p-5 rounded-2xl shadow-inner">
                    <label className="text-[10px] font-black text-brand-neonBlue uppercase tracking-widest ml-2 flex items-center gap-2">
                        <CheckCircle size={12} /> Automated Logistics Tasks
                    </label>
                    <p className="text-[10px] text-slate-400 ml-2 mb-3">
                        Optional action notes for this product, kept separate from equipment. Automatic task generation is not enabled in this workflow.
                    </p>
                    <input 
                        className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-sm text-white outline-none focus:border-brand-neonBlue transition-colors placeholder:text-slate-600" 
                        placeholder="e.g. Charge batteries, Pack backdrop, Send design template" 
                        value={formData.auto_tasks || ''} 
                        onChange={e => setFormData({...formData, auto_tasks: e.target.value})} 
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1 mt-6">Production Guide / Tech Notes</label>
                    <textarea 
                        className="w-full bg-slate-950 border border-slate-700 rounded p-2 text-white font-mono text-sm" 
                        rows={6} 
                        placeholder="## Setup Instructions..."
                        value={formData.productionGuide} 
                        onChange={e => setFormData({...formData, productionGuide: e.target.value})} 
                    />
                </div>

                {/* ⚡ COMBINED CLIENT RESOURCES SECTION */}
                <div className="bg-slate-900/30 p-5 rounded-2xl border border-white/5 space-y-6 shadow-inner">
                    
                    {/* External Design Link */}
                    <div>
                        <h4 className="text-white font-bold mb-2 flex items-center gap-2 text-sm">
                            <LinkIcon size={16} className="text-brand-neonBlue"/> External Design Link (e.g. Canva)
                        </h4>
                        <p className="text-[10px] text-slate-400 mb-3">
                            Provide a web link to a Canva template or external design guide.
                        </p>
                        <input 
                            className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3 text-white text-sm focus:border-brand-neonBlue outline-none transition-colors placeholder:text-slate-600" 
                            placeholder="https://canva.com/design/..." 
                            value={formData.uploadFolderUrl} 
                            onChange={e => setFormData({...formData, uploadFolderUrl: e.target.value})} 
                        />
                    </div>
                    
                    {/* Downloadable Docs */}
                    <div className="pt-6 border-t border-white/5">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h4 className="text-white font-bold flex items-center gap-2 text-sm">
                                    <Download size={16} className="text-brand-neonBlue"/> Downloadable Docs
                                </h4>
                                <p className="text-[10px] text-slate-400 mt-1">Physical files (PDFs, PSDs, specs) clients can download.</p>
                            </div>
                            <Button size="sm" variant="outline" className="rounded-xl" onClick={addDocument}>
                                <Plus size={14} className="mr-1"/> Add Doc
                            </Button>
                        </div>
                        <div className="space-y-3">
                            {formData.documents?.map(doc => (
                                <div key={doc.id} className="flex gap-3 items-center bg-slate-950 p-3 rounded-xl border border-white/10">
                                    <FileText size={18} className="text-slate-500 shrink-0"/>
                                    <input 
                                        className="bg-transparent text-white text-sm flex-1 border-none focus:ring-0 p-0 outline-none" 
                                        value={doc.name} 
                                        onChange={e => updateDocument(doc.id, { name: e.target.value })} 
                                        placeholder="Document Name" 
                                    />
                                    <button onClick={() => handleDocUploadClick(doc.id)} className="text-xs text-brand-neonBlue hover:text-brand-neonPink transition-colors font-bold px-2 shrink-0">
                                        {isUploading && activeDocUploadId === doc.id ? 'Uploading...' : (doc.url ? 'Change File' : 'Upload File')}
                                    </button>
                                    <button onClick={() => removeDocument(doc.id)} className="text-slate-500 hover:text-red-500 p-1 shrink-0 transition-colors">
                                        <X size={16}/>
                                    </button>
                                </div>
                            ))}
                            {formData.documents?.length === 0 && (
                                <div className="text-center py-6 border-2 border-dashed border-white/5 rounded-xl text-slate-600 text-xs font-bold uppercase tracking-widest">
                                    No physical documents added
                                </div>
                            )}
                            <input type="file" ref={docInputRef} className="hidden" onChange={handleDocFileChange} />
                        </div>
                    </div>
                </div>

{/* ⚡ NEW: DIGITAL SHARING TOGGLE ⚡ */}
<div className="bg-brand-neonBlue/5 border border-brand-neonBlue/30 p-5 rounded-2xl flex items-center justify-between shadow-inner">
                    <div>
                        <h4 className="text-white font-bold text-sm flex items-center gap-2">
                            <Smartphone size={16} className="text-brand-neonBlue"/> Requires Digital Sharing Setup?
                        </h4>
                        <p className="text-[10px] text-slate-400 mt-1 max-w-lg">
                            Check this if this product captures digital assets (like a photo booth). It will automatically ask the client to configure their SMS & Email sharing messages in the portal.
                        </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input 
                            type="checkbox" 
                            className="sr-only peer" 
                            checked={(formData as any).requiresSharingConfig || false}
                            onChange={e => setFormData({...formData, requiresSharingConfig: e.target.checked} as any)}
                        />
                        <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-neonBlue"></div>
                    </label>
                </div>

                {/* ⚡ EXISTING ONBOARDING FIELD BUILDER ⚡ */}
                <div className="border-t border-white/10 pt-8 pb-10">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h4 className="text-white font-bold flex items-center gap-2 text-lg"><List size={20} className="text-brand-neonBlue"/> Client Onboarding Form</h4>
                            <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mt-1">Questions asked when client logs into portal</p>
                        </div>
                        <Button size="sm" onClick={addField} className="bg-brand-neonBlue text-slate-950 font-bold shadow-neon rounded-xl"><Plus size={16} className="mr-1"/> Add Question</Button>
                    </div>
                    
                    <div className="space-y-4">
                        {formData.onboardingFields?.map((field, idx) => (
                            <div key={idx} className="bg-slate-900/60 p-5 rounded-2xl border border-white/10 flex flex-col gap-4 shadow-lg transition-all hover:border-brand-neonBlue/30">
                                <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                                    <div className="flex-1 w-full space-y-1.5">
                                        <label className="text-[9px] uppercase tracking-widest font-black text-brand-neonBlue ml-1">Question / Prompt</label>
                                        <input 
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-sm focus:border-brand-neonBlue outline-none transition-colors" 
                                            value={field.label} 
                                            onChange={e => updateField(idx, { label: e.target.value })} 
                                            placeholder="e.g. What size frame do you require?" 
                                        />
                                    </div>
                                    <div className="w-full md:w-48 space-y-1.5 shrink-0">
                                        <label className="text-[9px] uppercase tracking-widest font-black text-slate-500 ml-1">Response Type</label>
                                        <select 
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-sm focus:border-brand-neonBlue outline-none transition-colors appearance-none cursor-pointer" 
                                            value={field.type} 
                                            onChange={e => updateField(idx, { type: e.target.value as any })}
                                        >
                                            <option value="text">Short Text</option>
                                            <option value="textarea">Long Text Area</option>
                                            <option value="select">Multiple Choice</option>
                                            <option value="upload">File Upload</option>
                                            <option value="date">Date</option>
                                            <option value="color">Color Picker</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-2 mt-4 md:mt-5 shrink-0">
    <button 
        onClick={() => moveField(idx, 'up')} 
        disabled={idx === 0} 
        className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-500 hover:text-brand-neonBlue transition-all disabled:opacity-30 disabled:hover:text-slate-500 cursor-pointer disabled:cursor-not-allowed"
        title="Move Up"
    >
        <ChevronUp size={18}/>
    </button>
    <button 
        onClick={() => moveField(idx, 'down')} 
        disabled={idx === (formData.onboardingFields?.length || 0) - 1} 
        className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-500 hover:text-brand-neonBlue transition-all disabled:opacity-30 disabled:hover:text-slate-500 cursor-pointer disabled:cursor-not-allowed"
        title="Move Down"
    >
        <ChevronDown size={18}/>
    </button>
    <button 
        onClick={() => removeField(idx)} 
        className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-slate-500 hover:text-rose-500 hover:bg-rose-500/10 transition-all"
        title="Delete Question"
    >
        <Trash2 size={18}/>
    </button>
</div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/5">
                                    <div className="flex gap-4 items-center h-full pl-2">
                                        <label className="flex items-center gap-3 text-sm font-bold text-white cursor-pointer group">
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${field.required ? 'bg-brand-neonBlue border-brand-neonBlue text-slate-950' : 'bg-slate-950 border-slate-600 group-hover:border-brand-neonBlue'}`}>
                                                {field.required && <CheckSquare size={14} />}
                                            </div>
                                            <input type="checkbox" className="hidden" checked={field.required} onChange={e => updateField(idx, { required: e.target.checked })} /> 
                                            Mandatory Question
                                        </label>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] uppercase tracking-widest font-black text-slate-500 ml-1">Help Text (Optional)</label>
                                        <input 
                                            className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs focus:border-brand-neonBlue outline-none transition-colors" 
                                            value={field.helpText || ''} 
                                            onChange={e => updateField(idx, { helpText: e.target.value })} 
                                            placeholder="e.g. Please upload a transparent PNG logo." 
                                        />
                                    </div>
                                </div>

                                {/* ⚡ NEW VISUAL MULTI-CHOICE BUILDER ⚡ */}
                                {field.type === 'select' && (
                                    <div className="mt-2 bg-slate-950 border border-slate-800 p-4 rounded-xl shadow-inner animate-fade-in">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-brand-neonBlue flex items-center gap-2 mb-3">
                                            <ListChecks size={14} /> Configure Multiple Choice Options
                                        </label>
                                        
                                        {/* Render existing options */}
                                        {field.options && field.options.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {field.options.split(',').map((opt: string, optIdx: number) => {
                                                    const cleanOpt = opt.trim();
                                                    if (!cleanOpt) return null;
                                                    return (
                                                        <span key={optIdx} className="text-xs bg-slate-800 border border-white/10 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-md">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-brand-neonBlue"></span>
                                                            {cleanOpt} 
                                                            <button 
                                                                onClick={() => {
                                                                    const optArray = field.options!.split(',').map((o: string) => o.trim()).filter((o: string) => o);
                                                                    optArray.splice(optIdx, 1);
                                                                    updateField(idx, { options: optArray.join(',') });
                                                                }} 
                                                                className="text-slate-500 hover:text-rose-400 transition-colors ml-1"
                                                            >
                                                                <X size={12}/>
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Add new option input */}
                                        <div className="flex gap-2 relative">
                                            <input 
                                                className="w-full bg-slate-900 border border-white/5 rounded-lg p-3 text-white text-xs focus:border-brand-neonBlue outline-none transition-colors" 
                                                placeholder="Type a new choice and press Enter..." 
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        const newVal = e.currentTarget.value.trim();
                                                        if (newVal) {
                                                            const currentOpts = field.options ? field.options.split(',').map((o:string)=>o.trim()).filter((o:string)=>o) : [];
                                                            if (!currentOpts.includes(newVal)) {
                                                                updateField(idx, { options: [...currentOpts, newVal].join(',') });
                                                            }
                                                            e.currentTarget.value = '';
                                                        }
                                                    }
                                                }}
                                            />
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-black uppercase text-slate-500 tracking-widest pointer-events-none">
                                                Press Enter ↵
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {formData.onboardingFields?.length === 0 && (
                            <div className="text-center py-10 border-2 border-dashed border-white/10 rounded-2xl text-slate-500 font-bold text-xs uppercase tracking-widest">
                                No questions configured.
                            </div>
                        )}
                    </div>
                </div>
             </div>
             )}
          </>}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-slate-950/50 flex justify-end gap-3 shrink-0 rounded-b-2xl">
            <Button variant="outline" onClick={onClose} disabled={isSaving || isUploading}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving || isUploading || writerOpen}>
                {isSaving ? <><Loader2 size={16} className="animate-spin mr-2"/> Saving...</> : 'Save Product'}
            </Button>
        </div>
      </div>
    </div>
  );
};
